import { useState, useRef, useEffect } from "react";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { getMediaTypeFromUrl } from "../helper/mediaType";
import VideoCell from "./VideoCell";

interface AudioCellProps {
  link: string;
  customFileName?: string;
}

const AudioCell: React.FC<AudioCellProps> = ({ link, customFileName }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [downloadLoader, setDownloadLoader] = useState(false);

  const togglePlayPause = () => {
    if (!link) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(link);
      audioRef.current.onended = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }

    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
  }, []);

  const handleDownloadFile = (url: string, customFilename?: string) => {
    const link = document.createElement("a");
    link.href = url;

    if (customFilename) {
      link.download = customFilename;
    } else {
      link.download = url.split("/").pop()?.split("?")[0] || "download";
    }

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return getMediaTypeFromUrl(link) === "audio" ? (
    <Box display={"flex"}>
      <Tooltip title={downloadLoader ? "Downloading..." : "Download"}>
        <IconButton
          onClick={() => handleDownloadFile(link, customFileName)}
          disabled={!link}
          color="primary"
          size="small"
        >
          {downloadLoader ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <DownloadForOfflineIcon />
          )}
        </IconButton>
      </Tooltip>

      {link && (
        <Tooltip title={isPlaying ? "Pause" : "Play"}>
          <IconButton onClick={togglePlayPause} color="primary" size="small">
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ) : (
    <VideoCell link={link} />
  );
};

export default AudioCell;
